/**
 * useApprovalDecision — POST an Approve/Reject decision to Graph with optimistic
 * UI + badge refresh.
 *
 * The hook is intentionally callback-shaped (vs. mutation-object) so the
 * confirmation modal can fire it imperatively and await the result.
 */

import { useCallback, useState } from 'react';
import {
  submitDecision,
  type ApprovalDecision,
  type DecisionPayload,
} from '../services/graphApprovalsClient';
import { refreshApprovalsBadge } from './useApprovalsBadge';

export interface UseApprovalDecisionResult {
  submitting: boolean;
  error: string | null;
  /**
   * Submit a decision. Resolves to `{ ok: true }` on success or
   * `{ ok: false, error }` on failure. Side-effects: clears local error on
   * start, refreshes the badge poller on success.
   */
  submit: (
    approvalId: string,
    stageId: string,
    decision: ApprovalDecision,
    justification?: string,
  ) => Promise<{ ok: true; isLive: boolean } | { ok: false; error: string }>;
}

export function useApprovalDecision(): UseApprovalDecisionResult {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(
    async (
      approvalId: string,
      stageId: string,
      decision: ApprovalDecision,
      justification?: string,
    ): Promise<{ ok: true; isLive: boolean } | { ok: false; error: string }> => {
      setSubmitting(true);
      setError(null);
      try {
        const payload: DecisionPayload = { decision };
        if (justification && justification.trim()) payload.justification = justification.trim();
        const result = await submitDecision(approvalId, stageId, payload);
        if (!result.ok) {
          setError(result.error);
          return { ok: false, error: result.error };
        }
        // Re-poll the badge so the rail count updates immediately.
        refreshApprovalsBadge();
        return { ok: true, isLive: result.isLive };
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        return { ok: false, error: msg };
      } finally {
        setSubmitting(false);
      }
    },
    [],
  );

  return { submitting, error, submit };
}
